/**
 * Centralized cookie utilities with SSR safety
 */

/**
 * Get a cookie value by name
 */
export const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  
  const cookieValue = document.cookie
    .split(';')
    .find((row) => row.trim().startsWith(name + '='));
    
  return cookieValue ? decodeURIComponent(cookieValue.split('=')[1]) : null;
};

/**
 * Set a cookie value
 */
export const setCookie = (name: string, value: string, options?: {
  expires?: Date;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const defaultOptions = {
    expires: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
    path: '/',
    domain: undefined,
    secure: false,
    sameSite: 'Lax' as const
  };

  const finalOptions = { ...defaultOptions, ...options };

  let cookieString = `${name}=${encodeURIComponent(value)}`;
  
  if (finalOptions.expires) {
    cookieString += `; expires=${finalOptions.expires.toUTCString()}`;
  }
  
  if (finalOptions.path) {
    cookieString += `; path=${finalOptions.path}`;
  }
  
  if (finalOptions.domain) {
    cookieString += `; domain=${finalOptions.domain}`;
  }
  
  if (finalOptions.secure) {
    cookieString += '; secure';
  }
  
  if (finalOptions.sameSite) {
    cookieString += `; SameSite=${finalOptions.sameSite}`;
  }

  document.cookie = cookieString;
};

/**
 * Remove a cookie by name
 */
export const removeCookie = (cookieToRemove: string, path: string = '/'): void => {
  if (typeof document === 'undefined') {
    return;
  }
  
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
  document.cookie = `${cookieToRemove}=; expires=${expires}; path=${path};`;
  document.cookie = `${cookieToRemove}=; expires=${expires};`;
};

/**
 * Cleanup duplicate cookies
 */
export const cleanupDuplicateCookies = (cookieName: string, value?: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const allCookies = document.cookie.split(';');
  const cookieInstances = allCookies.filter(cookie => 
    cookie.trim().startsWith(`${cookieName}=`)
  );

  if (cookieInstances.length > 1) {
    cookieInstances.forEach(() => {
      removeCookie(cookieName, '/');
      removeCookie(cookieName, '');
      removeCookie(cookieName, window.location.pathname);
    });

    if (value) {
      setCookie(cookieName, value, { path: '/' });
    }
  }
};
